import { useState, useEffect, useRef } from 'react';

const COMMANDS = [
  {
    cmd: 'useradd devuser',
    output: [{ text: 'User devuser created successfully', color: 'text-emerald-400' }]
  },
  {
    cmd: 'yum install httpd -y',
    output: [
      { text: 'Resolving Dependencies...', color: 'text-slate-400' },
      { text: 'Installing: httpd-2.4.57-5.el9.x86_64', color: 'text-slate-400' },
      { text: 'Complete!', color: 'text-emerald-400' }
    ]
  },
  {
    cmd: 'systemctl start httpd',
    output: [{ text: '● httpd.service - Active: active (running)', color: 'text-emerald-400' }]
  },
  {
    cmd: 'chmod 755 /var/www/html',
    output: [{ text: 'Permissions updated', color: 'text-emerald-400' }]
  },
  {
    cmd: 'docker run -d nginx',
    output: [
      { text: 'Pulling from library/nginx...', color: 'text-slate-400' },
      { text: 'Status: Downloaded newer image', color: 'text-emerald-400' },
      { text: 'Container a3f8d2e started', color: 'text-emerald-400' }
    ]
  },
  {
    cmd: 'kubectl get pods',
    output: [
      { text: 'NAME              READY   STATUS    AGE', color: 'text-slate-400' },
      { text: 'nginx-pod         1/1     Running   2m', color: 'text-emerald-400' },
      { text: 'redis-master      1/1     Running   5m', color: 'text-emerald-400' }
    ]
  }
];

export function TerminalAnimation() {
  const [lines, setLines] = useState([]);
  const [typingText, setTypingText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const cancelledRef = useRef(false);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    cancelledRef.current = false;
    timeoutsRef.current = [];

    const cursorInterval = setInterval(() => {
      setCursorVisible(v => !v);
    }, 530);

    startAnimation();

    return () => {
      cancelledRef.current = true;
      clearInterval(cursorInterval);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      setLines([]);
      setTypingText('');
    };
  }, []);

  const delay = (ms) => new Promise(resolve => {
    const id = setTimeout(resolve, ms);
    timeoutsRef.current.push(id);
  });

  const startAnimation = async () => {
    let cmdIdx = 0;

    while (!cancelledRef.current) {
      const command = COMMANDS[cmdIdx % COMMANDS.length];

      // Type each character
      for (let i = 0; i <= command.cmd.length; i++) {
        if (cancelledRef.current) return;
        setTypingText(command.cmd.slice(0, i));
        await delay(55 + Math.random() * 25);
        if (cancelledRef.current) return;
      }

      // Pause after typing
      await delay(500);
      if (cancelledRef.current) return;

      // Move typed command into history
      setTypingText('');
      setLines(prev => {
        const next = [...prev, { text: `$ ${command.cmd}`, color: 'text-slate-400' }];
        return next.length > 6 ? next.slice(-4) : next;
      });

      // Show output lines
      for (const line of command.output) {
        await delay(350);
        if (cancelledRef.current) return;
        setLines(prev => {
          const next = [...prev, line];
          return next.length > 6 ? next.slice(-4) : next;
        });
      }

      // Pause before next command
      await delay(2200);
      if (cancelledRef.current) return;

      cmdIdx++;
    }
  };

  return (
    <div className="mt-10 max-w-3xl mx-auto" data-testid="terminal-animation">
      <div className="bg-slate-900 rounded-xl border border-slate-800 shadow-2xl overflow-hidden">
        <div className="bg-slate-800 px-3 py-2 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
          </div>
          <span className="text-xs text-slate-500 ml-2 font-mono">student@lab-vm ~</span>
        </div>
        <div className="p-4 font-mono text-xs sm:text-sm min-h-[150px] flex flex-col justify-end">
          {lines.map((line, i) => (
            <div key={i} className={`${line.color} leading-relaxed`}>
              {line.text}
            </div>
          ))}
          <div className="text-slate-400 leading-relaxed">
            <span>$ </span>
            <span className="text-slate-200">{typingText}</span>
            <span className={`text-emerald-400 font-bold ${cursorVisible ? 'opacity-100' : 'opacity-0'}`}>_</span>
          </div>
        </div>
      </div>
    </div>
  );
}
